<script setup lang="ts">
/**
 * 套餐档次徽章。
 *
 * 三处使用：Header 右上角、额度条内（`QuotaBar` 的 `trailing` 插槽）、以及将来任何
 * 需要"一眼看出这是哪个档"的地方。
 *
 * 徽章本身就是入口：点它去 `/checkout`。让用户看到"我是 Lite"之后还要自己找购买页，
 * 是这类展示最常见的浪费——尤其是在它恰好变成琥珀色、正在提示即将到期的时候。
 *
 * 配色与文案全来自 `lib/subscription.ts`（可测），这里只负责把 tone 映射成 class。
 */

import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { planLabelOf, type PlanId } from '@spirit/contracts';

import { cn } from '../../lib/cn';
import { formatDateTime } from '../../lib/format';
import { describeExpiry, type ExpiryStatus, type PlanBadgeTone } from '../../lib/subscription';

const props = withDefaults(
  defineProps<{
    planId: PlanId;
    tone: PlanBadgeTone;
    status: ExpiryStatus;
    size?: 'sm' | 'md';
  }>(),
  { size: 'sm' },
);

const TONE_CLASS: Readonly<Record<PlanBadgeTone, string>> = {
  free: 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200',
  paid: 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100',
  unlimited: 'border-accent-200 bg-accent-50 text-accent-700 hover:bg-accent-100',
  warning: 'border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200',
};

const label = computed(() => planLabelOf(props.planId));

/** 悬停时给出完整信息：徽章本体只放得下档位名，到期时间与"点它做什么"放 title。 */
const title = computed(() => {
  if (props.status.kind === 'none') {
    return `当前套餐：${label.value}。点击查看套餐与购买`;
  }
  return `当前套餐：${label.value}，${describeExpiry(props.status)}（${formatDateTime(props.status.expiresAt ?? '')}）。点击续费或升级`;
});
</script>

<template>
  <RouterLink
    to="/checkout"
    :title="title"
    :class="
      cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border font-medium transition-colors',
        TONE_CLASS[props.tone],
        props.size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
      )
    "
  >
    <span class="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />
    {{ label }}
  </RouterLink>
</template>
